import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Page-wide time scrubber (Palantir Foundry pattern). Drag the dot, every
// visible value re-renders to that point. [LIVE] snaps back and resumes
// streaming. Range pills on the right.

const RANGES = ['1H', '24H', '7D', '30D', '90D', '1Y', 'CUSTOM'] as const;
const RANGE_KEY: Record<(typeof RANGES)[number], string> = {
  '1H': 'shell.timeScrubber.range.1h',
  '24H': 'shell.timeScrubber.range.24h',
  '7D': 'shell.timeScrubber.range.7d',
  '30D': 'shell.timeScrubber.range.30d',
  '90D': 'shell.timeScrubber.range.90d',
  '1Y': 'shell.timeScrubber.range.1y',
  CUSTOM: 'shell.timeScrubber.range.custom',
};

export function TimeScrubber() {
  const { t } = useTranslation();
  const [pos, setPos] = useState(96); // 0..100 (% of window)
  const [range, setRange] = useState<(typeof RANGES)[number]>('24H');
  const live = pos >= 99;

  return (
    <div
      style={{
        height: 32,
        flexShrink: 0,
        borderTop: '1px solid var(--bd)',
        background: 'var(--bg-pane)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 12px',
        fontSize: 11,
      }}
    >
      <span className="h">{t('shell.timeScrubber.time')}</span>

      {/* slider */}
      <div
        style={{
          flex: 1,
          height: 6,
          background: 'var(--bd)',
          position: 'relative',
          cursor: 'pointer',
        }}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setPos(Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)));
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${pos}%`,
            background: 'var(--bd-strong)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `${pos}%`,
            top: -3,
            bottom: -3,
            width: 2,
            background: 'var(--primary)',
            transform: 'translateX(-1px)',
          }}
        />
      </div>

      <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-2)' }}>
        2026-04-29 14:32:01 UTC
      </span>

      {/* range pills */}
      <div style={{ display: 'flex', gap: 2 }}>
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            style={{
              height: 22,
              padding: '0 8px',
              fontSize: 10,
              border: '1px solid var(--bd-strong)',
              fontFamily: 'var(--mono)',
              letterSpacing: 0.4,
              color: range === r ? 'var(--primary)' : 'var(--fg-2)',
              background: range === r ? 'var(--primary-soft)' : 'var(--bg-pane-2)',
            }}
          >
            {t(RANGE_KEY[r])}
          </button>
        ))}
      </div>

      {/* LIVE button */}
      <button
        type="button"
        onClick={() => setPos(100)}
        className={`pill ${live ? 'green' : ''}`}
        style={{ height: 22, fontFamily: 'var(--mono)', fontSize: 10 }}
      >
        <span className="dot" /> {t('shell.timeScrubber.live')}
      </button>

      <span style={{ fontFamily: 'var(--mono)', color: 'var(--green)' }}>
        {t('shell.meta.deltaBase')}
      </span>
    </div>
  );
}
